import React from 'react';

import Card from '../../components/Card/Card';
import FundManagerSummary from '../../components/FundManagerSummary/FundManagerSummary';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import FundDetailSplitRow from '../../components/FundDetailSplitRow/FundDetailSplitRow';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import TruncatedDisclaimer from '../../components/TruncatedDisclaimer/TruncatedDisclaimer';
import {
    investmentObjectiveCardBottomUtil,
    investmentManagersDataUtil
} from './util';
import { generalizeFund } from '../../utils/fund-info';
import NavScrollContext from '../../components/PageNavigation/navigation-context';
import { TRANSPARENCY_TYPES } from '../../utils/constants';
import styles from './InvestmentObjective.css';
import detailStyles from './DetailStyles.css';
import {
    FUND_MANAGER,
    ETF_FUND_MANAGER,
    INVESTMENT_OBJECTIVE_KEY_FACTS_BOTTOM,
    ETF_INVESTMENT_OBJECTIVE_KEY_FACTS_BOTTOM,
    INVESTMENT_OBJECTIVE_KEY_FACTS_TOP,
    ETF_INVESTMENT_OBJECTIVE_KEY_FACTS_TOP
} from './queries';

const ETFDisclaimer = () => (
    <React.Fragment>
        <div className="dashed-divider" />
        <TruncatedDisclaimer
            title="This ETF is different from traditional ETFs."
            maxLines={4}
        >
            <div className="contents">
                <p>
                    Traditional ETFs tell the public what assets they hold each
                    day. This ETF will not. This may{' '}
                    <strong>create additional risks</strong>&nbsp; for your
                    investment. For example:&nbsp;
                </p>

                <ul>
                    <li>
                        <p>
                            You may have to pay more money to trade the ETF’s
                            shares. This ETF will provide less information to
                            traders, who tend to charge more for trades when
                            they have less information.&nbsp;
                        </p>
                    </li>
                    <li>
                        <p>
                            The price you pay to buy ETF shares on an exchange
                            may not match the value of the ETF’s portfolio. The
                            same is true when you sell shares. These price
                            differences may be greater for this ETF compared to
                            other ETFs because it provides less information to
                            traders.
                        </p>
                    </li>
                    <li>
                        <p>
                            These additional risks may be even greater in bad or
                            uncertain market conditions.
                        </p>
                    </li>
                    <li>
                        <p>
                            The ETF will publish on its website each day a
                            “Proxy Portfolio” designed to help trading in shares
                            of the ETF. While the Proxy Portfolio includes some
                            of the ETF’s holdings, it is not the ETF’s actual
                            portfolio.
                        </p>
                    </li>
                </ul>
                <p>
                    The differences between this ETF and other ETFs may also
                    have advantages. By keeping certain information about the
                    ETF secret, this ETF may face less risk that other traders
                    can predict or copy its investment strategy. This may
                    improve the ETF’s performance. If other traders are able to
                    copy or predict the ETF’s investment strategy, however, this
                    may hurt the ETF’s performance.
                </p>
                <p>
                    For additional information regarding the unique attributes
                    and risks of the ETF, see the fund&apos;s prospectus.
                </p>
            </div>
        </TruncatedDisclaimer>
    </React.Fragment>
);

const InvestmentObjectiveCard = (props) => {
    const { ticker, iClass, etf, transparencyTypeName } = props;

    return (
        <div className="section-container">
            <div>
                <Card>
                    <GqlQueryWrapper
                        query={
                            etf
                                ? ETF_INVESTMENT_OBJECTIVE_KEY_FACTS_TOP
                                : INVESTMENT_OBJECTIVE_KEY_FACTS_TOP
                        }
                        variables={{ ticker }}
                        dataFunc={generalizeFund}
                        placeholder={() => <TablePreRender height="100px" />}
                        unbatch
                    >
                        {({ data: { fund } }) => (
                            <div className="text-container">
                                <TitleWithDescription
                                    title="Investment Objective"
                                    id="inv-obj"
                                    borderLine
                                >
                                    <div
                                        className="text"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: fund.objective
                                        }}
                                    />
                                </TitleWithDescription>
                                {Boolean(fund.strategyText) && (
                                    <TitleWithDescription
                                        title="Strategy"
                                        borderLine
                                    >
                                        <div
                                            className="text"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: fund.strategyText
                                            }}
                                        />
                                    </TitleWithDescription>
                                )}
                                {Boolean(fund.riskRewardText) && (
                                    <TitleWithDescription
                                        title="Risks"
                                        borderLine
                                    >
                                        <div
                                            className="text"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: fund.riskRewardText
                                            }}
                                        />
                                        {!etf && (
                                            <NavScrollContext.Consumer>
                                                {(scrollTo) => (
                                                    <a
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            scrollTo(3);
                                                        }}
                                                        className="see-rr-section"
                                                        href="#content-ratings-risks"
                                                    >
                                                        See Ratings & Risks
                                                        section for more details
                                                    </a>
                                                )}
                                            </NavScrollContext.Consumer>
                                        )}
                                    </TitleWithDescription>
                                )}
                            </div>
                        )}
                    </GqlQueryWrapper>

                    <div className="manager-container">
                        <GqlQueryWrapper
                            query={etf ? ETF_FUND_MANAGER : FUND_MANAGER}
                            variables={{ ticker }}
                            dataFunc={investmentManagersDataUtil}
                            placeholder={() => (
                                <TablePreRender height="100px" />
                            )}
                        >
                            {({ data }) => (
                                <FundManagerSummary
                                    data={data}
                                    ticker={ticker}
                                />
                            )}
                        </GqlQueryWrapper>
                    </div>

                    <div className="subsection">
                        <GqlQueryWrapper
                            query={
                                etf
                                    ? ETF_INVESTMENT_OBJECTIVE_KEY_FACTS_BOTTOM
                                    : INVESTMENT_OBJECTIVE_KEY_FACTS_BOTTOM
                            }
                            variables={{ ticker }}
                            dataFunc={investmentObjectiveCardBottomUtil(iClass)}
                            placeholder={() => (
                                <TablePreRender height="100px" />
                            )}
                        >
                            {({ data }) =>
                                data.map((row) => (
                                    <FundDetailSplitRow
                                        key={`fdsr-${row
                                            .map((x) => x.label)
                                            .join()}`}
                                        data={row}
                                        styles={detailStyles}
                                    />
                                ))
                            }
                        </GqlQueryWrapper>
                    </div>
                    {etf && transparencyTypeName === TRANSPARENCY_TYPES.SEMI ? (
                        <ETFDisclaimer />
                    ) : null}
                </Card>
            </div>
            <style jsx>{styles}</style>
        </div>
    );
};

export default InvestmentObjectiveCard;
